import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchLeaveRequests } from '../../../services/employee';
import CustomPagination from '../../../components/common/CustomPagination.js';

const LeaveRequests = () => {
  const { t } = useTranslation(['common', 'employees']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filters, setFilters] = useState({
    employee: '',
    status: '',
    leave_type: '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchLeaveRequests(params);
      
      if (response.results) {
        setLeaveRequests(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setLeaveRequests(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch leave requests');
      setLeaveRequests([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return leaveRequests.map((req, index) => ({
      ...req,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [leaveRequests, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('employees:leaveRequests.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_name',
      headerName: t('employees:leaveRequests.columns.employee'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'employee_id',
      headerName: t('employees:leaveRequests.columns.employeeId'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'leave_type_name',
      headerName: t('employees:leaveRequests.columns.leaveType'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'start_date',
      headerName: t('employees:leaveRequests.columns.startDate'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'end_date',
      headerName: t('employees:leaveRequests.columns.endDate'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'total_days',
      headerName: t('employees:leaveRequests.columns.days'),
      width: 80,
      sortable: false,
      renderCell: (params) => params.value || 0
    },
    {
      field: 'status',
      headerName: t('employees:leaveRequests.columns.status'),
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const status = params.value || 'pending';
        const colorMap = {
          'pending': 'warning',
          'approved_by_manager': 'info',
          'approved_by_hr': 'info',
          'approved_by_director': 'success',
          'rejected': 'error',
          'cancelled': 'default'
        };
        const labelMap = {
          'pending': t('employees:leaveRequests.status.pending'),
          'approved_by_manager': t('employees:leaveRequests.status.approvedByManager'),
          'approved_by_hr': t('employees:leaveRequests.status.approvedByHR'),
          'approved_by_director': t('employees:leaveRequests.status.approvedByDirector'),
          'rejected': t('employees:leaveRequests.status.rejected'),
          'cancelled': t('employees:leaveRequests.status.cancelled')
        };
        return (
          <Chip
            label={labelMap[status] || status}
            color={colorMap[status] || 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'requested_at',
      headerName: t('employees:leaveRequests.columns.requested'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy HH:mm');
        } catch {
          return 'N/A';
        }
      }
    }
  ], [t]);

  if (loading && leaveRequests.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('employees:leaveRequests.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:leaveRequests.employee')}
                fullWidth
                value={filters.employee}
                onChange={(e) => handleFilterChange('employee', e.target.value)}
                placeholder={t('employees:leaveRequests.employeePlaceholder')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label={t('employees:status')}
                fullWidth
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="pending">{t('employees:leaveRequests.status.pending')}</option>
                <option value="approved_by_manager">{t('employees:leaveRequests.status.approvedByManager')}</option>
                <option value="approved_by_hr">{t('employees:leaveRequests.status.approvedByHR')}</option>
                <option value="approved_by_director">{t('employees:leaveRequests.status.approvedByDirector')}</option>
                <option value="rejected">{t('employees:leaveRequests.status.rejected')}</option>
                <option value="cancelled">{t('employees:leaveRequests.status.cancelled')}</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:leaveRequests.startDate')}
                fullWidth
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:leaveRequests.endDate')}
                fullWidth
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default LeaveRequests;

